<template>
    <WrapTableScroll
        classBody="w-full min-w-min"
        classWrap="min-w-min"
        space="max-mob:px-4 max-mob:-mx-4"
    >
        <div class="relative w-max min-w-full">
            <CustomTableControls
                :showMultipleActions="showMultipleActions"
                :headerType="SETTINGS_HEADER_TABLE.blockedCarrier"
                class="pb-2"
                mainBg="bg-white dark:bg-blue-850"
                showOnPage
                :searchTimeout="0"
                :onPage="onPage"
                :onPageConfig="{ max: 100, min: 10 }"
                showSearch
                :dropdownButtonConfig="{ name: 'Block Carrier' }"
                :showButton="editVoipShieldPermission"
                @onChangeOnPage="changeOnPage"
                @onAdd="openBlockCarrierDialog"
                @onSearch="onSearch"
            >

                <template #multipleActions>
                    <DeleteIcon
                        v-if="editVoipShieldPermission"
                        class="text-gray-200 hover:text-purple transition-colors cursor-pointer"
                        v-tippy="getTippyTable('Delete')"
                        @click="openSelectDeleteDialog"
                    />
                </template>
            </CustomTableControls>

            <CustomDataTable
                v-model:page="dataPagination.page"
                v-model:selected="selected"
                :headers="headers"
                :items="displayedItems"
                :loading="loading"
                :total="dataPagination.total"
                offSticky
                showSelect
                mainBg="bg-white dark:bg-blue-850"
            >
                <template #item.actions="{ item }">
                    <div class="flex items-center justify-center gap-x-1.5">
                        <DetailAction
                            class="text-gray-200 hover:text-purple transition-colors cursor-pointer"
                            :edit="editVoipShieldPermission"
                            @click.stop="openEditDialog(item)"
                        />

                        <DeleteIcon
                            v-if="editVoipShieldPermission"
                            class="text-gray-200 hover:text-purple transition-colors cursor-pointer"
                            v-tippy="getTippyTable('Delete')"
                            @click.stop="openDeleteDialog(item)"
                        />
                    </div>
                </template>
            </CustomDataTable>
        </div>
    </WrapTableScroll>
</template>

<script setup>
import {
    computed, ref, reactive, watch,
} from 'vue'

import WrapTableScroll from '@/components/scroll/WrapTableScroll.vue'
import CustomTableControls from '@/components/table/controls/CustomTableControls.vue'
import CustomDataTable from '@/components/table/CustomDataTable.vue'
import DeleteIcon from '@/components/icons/DeleteIcon.vue'
import DetailAction from '@/components/actions/DetailAction.vue'

import { localSearch } from '@/helpers/app-helper'
import { getTippyTable } from '@/helpers/tippy-helper'

import { usePermissionsStore } from '@/store/permissionsStore'

import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'

const props = defineProps({
    loading: {
        type: Boolean,
        default: false,
    },
    showMultipleActions: {
        type: Boolean,
        default: false,
    },
    items: {
        type: Array,
        default: () => [],
    },
    headers: {
        type: Array,
        default: () => [],
    },
})

const emits = defineEmits(['openBlockCarrierDialog', 'openDeleteDialog', 'openEditDialog', 'openSelectDeleteDialog'])

const permissionsStore = usePermissionsStore()

const onPage = defineModel('onPage', { type: Number, default: 25 })

const selected = defineModel('selected', { type: Array, default: () => [] })

const dataPagination = reactive({
    page: 1,
    total: 1,
})

const searchValue = ref('')

const editVoipShieldPermission = computed(() => permissionsStore.permissions.voipShield.edit)

const localItems = computed(() => localSearch(props.items, searchValue.value, ['name']))

const displayedItems = computed(() => {
    const startIndex = (dataPagination.page - 1) * onPage.value
    const endIndex = startIndex + onPage.value

    return localItems.value.slice(startIndex, endIndex)
})

function onSearch(search) {
    searchValue.value = search
}

function openBlockCarrierDialog() {
    emits('openBlockCarrierDialog')
}

function openEditDialog(item) {
    emits('openEditDialog', item)
}

function openDeleteDialog(item) {
    emits('openDeleteDialog', item)
}

function openSelectDeleteDialog() {
    emits('openSelectDeleteDialog')
}

function changeOnPage(value) {
    onPage.value = value
}

function setDataPagination() {
    dataPagination.page = 1
    dataPagination.total = Math.ceil(localItems.value.length / onPage.value)
}

watch(onPage, () => {
    setDataPagination()
})

watch(searchValue, () => {
    setDataPagination()
})

watch(() => localItems.value, () => {
    setDataPagination()
}, { immediate: true })
</script>
